/**
 * React Query hooks for Earthworks EW-6 invoices (fieldiq-api#1300).
 *
 * Same rules as useEwTickets: every key starts ['earthworks'], every write
 * invalidates that one prefix (an invoice moves the job's tickets, its
 * holdback, the billable queue and the quote's remaining draw together), and
 * every read spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a
 * refusal, never as an empty list. Finance → Invoices caches the other
 * verticals' rows under ['finance-invoices-other-verticals'], so a write
 * invalidates that too.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  EwBillableGroup,
  EwFromChangeOrderWrite,
  EwFromQuoteWrite,
  EwFromTicketsWrite,
  EwHoldback,
  EwQuoteDraws,
  EwPayLink,
  EwQboMappingNeeds,
  EwQboPushResult,
  EwInvoice,
  EwInvoiceDraftWrite,
  EwInvoiceFilters,
  EwInvoiceRow,
  EwPaymentWrite,
  EwReleaseHoldbackWrite,
} from '../types/ew-invoices';
import { invalidateEw } from './useEarthworks';

/** `data` from an envelope; `data: null` (or a missing key) stays null — never the envelope. */
function dataOf<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const b = body as { data?: unknown };
  return 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
}
function listOf<T>(body: unknown): T[] {
  const d = dataOf<unknown>(body);
  return Array.isArray(d) ? (d as T[]) : [];
}

const clean = (p?: object) => {
  if (!p) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null && v !== '') out[k] = String(v);
  return out;
};

export const ewInvoiceKeys = {
  invoices: (p?: EwInvoiceFilters) => ['earthworks', 'invoices', clean(p)] as const,
  invoice: (id: string) => ['earthworks', 'invoice', id] as const,
  billable: () => ['earthworks', 'invoices-billable'] as const,
  holdback: (jobId: string) => ['earthworks', 'holdback', jobId] as const,
  quoteDraws: (quoteId: string) => ['earthworks', 'quote-draws', quoteId] as const,
};

export function useEwInvoices(filters?: EwInvoiceFilters, enabled = true) {
  return useQuery({
    queryKey: ewInvoiceKeys.invoices(filters),
    queryFn: async () => listOf<EwInvoiceRow>((await api.earthworks.invoices.list(Object.keys(clean(filters)).length ? clean(filters) : undefined)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ewInvoiceKeys.invoice(id ?? ''),
    queryFn: async () => dataOf<EwInvoice>((await api.earthworks.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwBillable() {
  return useQuery({
    queryKey: ewInvoiceKeys.billable(),
    queryFn: async () => listOf<EwBillableGroup>((await api.earthworks.invoices.billable()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwHoldback(jobId: string | undefined) {
  return useQuery({
    queryKey: ewInvoiceKeys.holdback(jobId ?? ''),
    queryFn: async () => dataOf<EwHoldback>((await api.earthworks.jobHoldback(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwQuoteDraws(quoteId: string | undefined) {
  return useQuery({
    queryKey: ewInvoiceKeys.quoteDraws(quoteId ?? ''),
    queryFn: async () => dataOf<EwQuoteDraws>((await api.earthworks.quoteDraws(quoteId as string)).data),
    enabled: !!quoteId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useEwInvoiceWrite<V>(fn: (v: V) => Promise<EwInvoice | null>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      invalidateEw(qc);
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
    },
  });
}

export const useCreateEwInvoiceFromTickets = () =>
  useEwInvoiceWrite(async (body: EwFromTicketsWrite) => dataOf<EwInvoice>((await api.earthworks.invoices.fromTickets(body)).data));
export const useCreateEwInvoiceFromQuote = () =>
  useEwInvoiceWrite(async (body: EwFromQuoteWrite) => dataOf<EwInvoice>((await api.earthworks.invoices.fromQuote(body)).data));
export const useCreateEwInvoiceFromChangeOrder = () =>
  useEwInvoiceWrite(async (body: EwFromChangeOrderWrite) =>
    dataOf<EwInvoice>((await api.earthworks.invoices.fromChangeOrder(body)).data));
export const useReleaseEwHoldback = () =>
  useEwInvoiceWrite(async (body: EwReleaseHoldbackWrite) => dataOf<EwInvoice>((await api.earthworks.invoices.releaseHoldback(body)).data));
export const useUpdateEwInvoice = () =>
  useEwInvoiceWrite(async ({ id, body }: { id: string; body: EwInvoiceDraftWrite }) =>
    dataOf<EwInvoice>((await api.earthworks.invoices.update(id, body)).data));
export const useMarkEwInvoiceSent = () =>
  useEwInvoiceWrite(async (id: string) => dataOf<EwInvoice>((await api.earthworks.invoices.markSent(id)).data));
export const useRecordEwPayment = () =>
  useEwInvoiceWrite(async ({ id, body }: { id: string; body: EwPaymentWrite }) =>
    dataOf<EwInvoice>((await api.earthworks.invoices.recordPayment(id, body)).data));
export const useVoidEwInvoice = () =>
  useEwInvoiceWrite(async ({ id, reason }: { id: string; reason: string }) =>
    dataOf<EwInvoice>((await api.earthworks.invoices.void(id, { reason })).data));

/** EW-6b: the customer's "Pay now" link. Both refresh the invoice (its payLink). */
export function useCreateEwPayLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => dataOf<EwPayLink>((await api.earthworks.invoices.createPayLink(id)).data),
    onSuccess: () => invalidateEw(qc),
  });
}
export const useRevokeEwPayLink = () =>
  useEwInvoiceWrite(async (id: string) => dataOf<EwInvoice>((await api.earthworks.invoices.revokePayLink(id)).data));

/**
 * S10: push to QuickBooks. A refusal is the caller's to read (readQboPushRefusal);
 * either way the invoice refreshes, since a refusal can carry a QuickBooks id.
 */
export function usePushEwInvoiceToQbo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => dataOf<EwQboPushResult>((await api.earthworks.invoices.qboPush(id)).data),
    onSettled: () => invalidateEw(qc),
  });
}

/** S10: what the tenant's unpushed earthworks invoices need bound. Admin / office staff only. */
export function useEwQboMappingNeeds(enabled = true) {
  return useQuery({
    queryKey: ['earthworks', 'qbo-mapping-needs'] as const,
    queryFn: async () => dataOf<EwQboMappingNeeds>((await api.earthworks.qboMappingNeeds()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
